// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth, AppHeader } from '@umd-dit-ai-workspace/workspaces-component'
import { JobPoller } from '@umd-dit-ai-workspace/jobs-component'
import { discoverApi } from '@/api/discoverApi'
import type { WorkspaceSuggestion } from '@/api/discoverApi'
import { Sparkles, ChevronRight, Plus, CheckCircle, AlertCircle, ChevronDown, MessageCircle } from 'lucide-react'

export default function GetStartedPage() {
  const { accessibleOrganizations } = useAuth()

  // AI workspace suggest
  const [aiQuery, setAiQuery] = useState('')
  const [suggestJobId, setSuggestJobId] = useState<string | null>(null)
  const [suggestJobStatus, setSuggestJobStatus] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<WorkspaceSuggestion[] | null>(null)
  const [suggestError, setSuggestError] = useState<string | null>(null)
  const [requestingWs, setRequestingWs] = useState<WorkspaceSuggestion | null>(null)
  const [joinMessage, setJoinMessage] = useState('')
  const [joinedIds, setJoinedIds] = useState<Set<string>>(new Set())
  const [questioningWs, setQuestioningWs] = useState<WorkspaceSuggestion | null>(null)
  const [questionText, setQuestionText] = useState('')
  const [questionSentIds, setQuestionSentIds] = useState<Set<string>>(new Set())
  const wsKey = (ws: { orgId: string; wsId: string }) => `${ws.orgId}::${ws.wsId}`

  useEffect(() => {
    discoverApi.getMyRequests().then(pending => {
      const joinKeys = new Set<string>()
      for (const req of pending) {
        if (req.request_type === 'join' && req.org_id && req.ws_id) {
          joinKeys.add(`${req.org_id}::${req.ws_id}`)
        }
      }
      if (joinKeys.size > 0) setJoinedIds(joinKeys)
    }).catch(() => {/* non-blocking — silently ignore if it fails */})
  }, [])

  // Create workspace form
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createMessage, setCreateMessage] = useState('')
  const [createSubmitted, setCreateSubmitted] = useState(false)

  const defaultOrgId = accessibleOrganizations[0]?.orgId

  const suggestMutation = useMutation({
    mutationFn: () => discoverApi.suggestWorkspaces(aiQuery.trim()),
    onSuccess: (data) => {
      setSuggestError(null)
      setSuggestions(null)
      setSuggestJobId(data.job_id)
      setSuggestJobStatus('PENDING')
    },
    onError: (err: any) => setSuggestError(err?.message || 'Something went wrong, please try again.'),
  })

  const handleSuggestComplete = async () => {
    if (!suggestJobId) return
    const jobId = suggestJobId
    try {
      const data = await discoverApi.fetchSuggestResult(jobId)
      setSuggestions(data)
    } catch (err: any) {
      setSuggestError(err?.message || 'Failed to fetch suggestions.')
    } finally {
      setSuggestJobId(null)
      setSuggestJobStatus(null)
    }
  }

  const handleSuggestError = (err: Error) => {
    setSuggestError(err.message || 'Analysis failed. Please try again.')
    setSuggestJobId(null)
    setSuggestJobStatus(null)
  }

  const joinMutation = useMutation({
    mutationFn: (ws: WorkspaceSuggestion) =>
      discoverApi.submitRequest({
        org_id: ws.orgId,
        request_type: 'join',
        ws_id: ws.wsId,
        message: joinMessage.trim() || undefined,
      }),
    onSuccess: (_, ws) => {
      setJoinedIds(prev => new Set([...prev, wsKey(ws)]))
      setRequestingWs(null)
      setJoinMessage('')
    },
  })

  const askQuestionMutation = useMutation({
    mutationFn: (ws: WorkspaceSuggestion) =>
      discoverApi.askQuestion({
        org_id: ws.orgId,
        ws_id: ws.wsId,
        ws_name: ws.name,
        question: questionText.trim(),
      }),
    onSuccess: (_, ws) => {
      setQuestionSentIds(prev => new Set([...prev, wsKey(ws)]))
      setQuestioningWs(null)
      setQuestionText('')
    },
  })

  const createMutation = useMutation({
    mutationFn: () =>
      discoverApi.submitRequest({
        org_id: defaultOrgId || '',
        request_type: 'create',
        ws_name: createName.trim(),
        ws_description: createDescription.trim(),
        message: createMessage.trim() || undefined,
      }),
    onSuccess: () => {
      setCreateSubmitted(true)
      setCreateName('')
      setCreateDescription('')
      setCreateMessage('')
    },
  })

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        appId="course-align"
        navigationItems={[]}
        showDashboard={false}
        showWorkspaceSelector={false}
        debugMode={import.meta.env.DEV || import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
      />

      <div className="max-w-2xl mx-auto px-4 py-10 space-y-10">

        {/* Welcome */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Welcome to Course Align</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Your AI partner for aligning course materials with learning outcomes. Join a workspace to get started.
          </p>
        </div>

        {/* Video */}
        <div className="bg-black rounded-xl overflow-hidden shadow-lg">
          <video
            src="/course-align-demo-v6.mp4"
            className="w-full max-h-[480px] object-contain"
            controls
          />
        </div>

        {/* Find a workspace */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Find a Workspace</h2>
            <p className="text-sm text-gray-500 mt-1">
              Describe your course or need and AI will suggest the best matches.
            </p>
          </div>

          {/* Search input */}
          <div className="p-4 border-b border-gray-100 space-y-3">
            <textarea
              rows={3}
              placeholder="e.g. I'm teaching intro biology for non-majors, focused on cell and molecular topics…"
              value={aiQuery}
              onChange={e => { setAiQuery(e.target.value); setSuggestions(null) }}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <button
              onClick={() => { setSuggestions(null); setSuggestError(null); suggestMutation.mutate() }}
              disabled={suggestMutation.isPending || !!suggestJobId || !aiQuery.trim()}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {suggestMutation.isPending || suggestJobId ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Finding matches…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Find Workspaces
                </>
              )}
            </button>
            {suggestError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {suggestError}
              </p>
            )}
            {suggestJobId && (
              <JobPoller
                jobId={suggestJobId}
                jobContext={{}}
                onStatusChange={setSuggestJobStatus}
                onComplete={handleSuggestComplete}
                onError={handleSuggestError}
              />
            )}
          </div>

          {/* Suggestions list */}
          <div className="overflow-y-auto max-h-72">
            {suggestions === null ? (
              <div className="p-6 text-center text-sm text-gray-400">
                Describe what you're looking for above to get AI-matched suggestions.
              </div>
            ) : suggestions.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No matching workspaces found. Try a different description or request a new one below.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {suggestions.map(ws => {
                  const sent = joinedIds.has(wsKey(ws))
                  const isJoinExpanded = requestingWs ? wsKey(requestingWs) === wsKey(ws) : false
                  const isAskExpanded = questioningWs ? wsKey(questioningWs) === wsKey(ws) : false
                  const questionSent = questionSentIds.has(wsKey(ws))
                  return (
                    <li key={ws.wsId} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-1">
                          <p className="text-sm font-medium text-gray-800">{ws.name}</p>
                          <p className="text-xs text-blue-600 italic">{ws.reason}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {sent ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                              <CheckCircle className="h-3.5 w-3.5" />
                              Requested
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                if (isJoinExpanded) {
                                  setRequestingWs(null)
                                  setJoinMessage('')
                                } else {
                                  setRequestingWs(ws)
                                  setJoinMessage('')
                                  setQuestioningWs(null)
                                  setQuestionText('')
                                }
                              }}
                              className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                            >
                              Request
                              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isJoinExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          )}
                          {questionSent ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                              <CheckCircle className="h-3.5 w-3.5" />
                              Question Sent
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                if (isAskExpanded) {
                                  setQuestioningWs(null)
                                  setQuestionText('')
                                } else {
                                  setQuestioningWs(ws)
                                  setQuestionText('')
                                  setRequestingWs(null)
                                  setJoinMessage('')
                                }
                              }}
                              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                            >
                              <MessageCircle className="h-3.5 w-3.5" />
                              Ask
                              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isAskExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          )}
                        </div>
                      </div>

                      {isJoinExpanded && (
                        <div className="mt-3 space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Why do you want to join this workspace? <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            rows={2}
                            placeholder="e.g. I'm an instructor for this course and need to review the alignment materials…"
                            value={joinMessage}
                            onChange={e => setJoinMessage(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          />
                          {joinMutation.isError && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {(joinMutation.error as Error)?.message || 'Request failed'}
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => joinMutation.mutate(ws)}
                              disabled={joinMutation.isPending || !joinMessage.trim()}
                              className="flex-1 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {joinMutation.isPending ? 'Sending…' : 'Send Request'}
                            </button>
                            <button
                              onClick={() => { setRequestingWs(null); setJoinMessage('') }}
                              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {isAskExpanded && (
                        <div className="mt-3 space-y-2">
                          <textarea
                            rows={2}
                            placeholder="Type your question for the admin…"
                            value={questionText}
                            onChange={e => setQuestionText(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                          />
                          {askQuestionMutation.isError && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {(askQuestionMutation.error as Error)?.message || 'Failed to send question'}
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => askQuestionMutation.mutate(ws)}
                              disabled={askQuestionMutation.isPending || !questionText.trim()}
                              className="flex-1 py-1.5 text-xs font-medium bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {askQuestionMutation.isPending ? 'Sending…' : 'Send Question'}
                            </button>
                            <button
                              onClick={() => { setQuestioningWs(null); setQuestionText('') }}
                              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {/* Create new workspace — collapsible footer */}
          <div className="border-t border-gray-100">
            <button
              onClick={() => { setShowCreateForm(v => !v); setCreateSubmitted(false) }}
              className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <span>
                Don't see your workspace?{' '}
                <span className="text-blue-600 font-medium">Request a new one</span>
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showCreateForm ? 'rotate-180' : ''}`} />
            </button>

            {showCreateForm && (
              <div className="mx-4 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-0.5">
                <p><span className="font-semibold">Note:</span> Workspaces are created at the program level — one workspace per program.</p>
                <p>Users must complete the required Course Align training before being granted access to a workspace.</p>
              </div>
            )}

            {showCreateForm && (
              <div className="px-4 pb-4">
                {createSubmitted ? (
                  <div className="flex flex-col items-center gap-3 py-6 text-center">
                    <CheckCircle className="h-9 w-9 text-green-500" />
                    <p className="text-sm font-medium text-gray-800">Request submitted!</p>
                    <p className="text-xs text-gray-500">An admin will review your request and get back to you.</p>
                    <button
                      onClick={() => setCreateSubmitted(false)}
                      className="mt-1 text-xs text-blue-600 hover:underline"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={e => { e.preventDefault(); createMutation.mutate() }}
                    className="space-y-3"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Workspace name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. CHEM"
                        value={createName}
                        onChange={e => setCreateName(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Brief description of the program"
                        value={createDescription}
                        onChange={e => setCreateDescription(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Why do you need this workspace? <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={2}
                        required
                        placeholder="e.g. I am teaching courses program next semester and need a space to analyze and compare materials…"
                        value={createMessage}
                        onChange={e => setCreateMessage(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>

                    {createMutation.isError && (
                      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        {(createMutation.error as Error)?.message || 'Request failed. Please try again.'}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={createMutation.isPending || !createName.trim() || !createDescription.trim() || !createMessage.trim()}
                      className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {createMutation.isPending ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Submit Request
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400">
          Need help? Contact{' '}
          <a href="mailto:dit-ais@umd.edu" className="text-blue-500 hover:underline">
            dit-ais@umd.edu
          </a>
        </p>
      </div>
    </div>
  )
}
